<template>
    <div class="ai-debugger">
        <button
            type="button"
            class="ai-bubble"
            :class="{ 'new-message': unseenIds.length }"
            :title="$t('aiDebugger.title')"
            :aria-expanded="isPanelOpen"
            @click="open()"
        >
            <span class="counter-message" :style="unseenIds.length ? 'opacity: 1 !important' : 'opacity: 0 !important'">{{ unseenIds.length }}</span>
            <inline-svg
                class="ai-icon"
                :class="{ animated: runningCount && !unseenIds.length }"
                :src="require('@/assets/icons/robot-ai.svg')"
            />
        </button>

        <transition>
            <div v-if="isPanelOpen" ref="main" class="main ai-debugger-overlay" @click="onCloseClick($event)">
                <div ref="card" class="card-box" :class="isClosing ? 'popup-hidden' : 'popup-shown'" @animationend="onAnimationEnd">
                    <transition name="chat">
                        <div v-if="show" class="ai-card-content">
                            <div class="header">
                                <div class="title">
                                    <inline-svg class="title-icon" :src="require('@/assets/icons/robot-ai.svg')"/>
                                    {{ $t('aiDebugger.title') }}
                                </div>
                                <button type="button" class="btn-top btn-rotate" :title="$t('generic.close')" @click.stop="close()">
                                    <inline-svg class="svg-close" :src="require('@/assets/icons/plus.svg')"/>
                                </button>
                            </div>
                            <transition name="chat">
                                <AiDebuggerPanel v-if="showContent" class="ai-card-body" :highlight-ids="highlightIds"/>
                            </transition>
                        </div>
                    </transition>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import {mapActions, mapMutations, mapState} from 'vuex';
import {lazyComponent} from '@/helpers/lazyLoad';

const AiDebuggerPanel = lazyComponent(() => import('@/views/components/aiDebugger/AiDebuggerPanel.vue'));

export default {
    name: 'AiDebuggerBubble',
    components: {AiDebuggerPanel},
    data() {
        return {
            show: false,
            showContent: false,
            isClosing: false,
            highlightIds: [],
            timers: []
        };
    },
    computed: {
        ...mapState('aiDebugger', ['isPanelOpen', 'investigations', 'unseenIds']),
        runningCount() {
            return this.investigations.filter((investigation) => ['PENDING', 'RUNNING'].includes(investigation.status)).length;
        }
    },
    methods: {
        ...mapMutations('aiDebugger', ['setIsPanelOpen']),
        ...mapActions('aiDebugger', ['watchInvestigations', 'unwatchInvestigations']),
        later(callback, delay) {
            this.timers.push(setTimeout(callback, delay));
        },
        clearTimers() {
            this.timers.forEach((timer) => clearTimeout(timer));
            this.timers = [];
        },
        open() {
            if (this.isPanelOpen) return;
            this.clearTimers();
            this.isClosing = false;
            this.highlightIds = [...this.unseenIds];
            this.setIsPanelOpen(true);
            this.later(() => {
                this.show = true;
            }, 220);
            this.later(() => {
                this.showContent = true;
            }, 300);
        },
        close() {
            if (!this.isPanelOpen || this.isClosing) return;
            this.clearTimers();
            this.showContent = false;
            this.later(() => {
                this.show = false;
                this.isClosing = true;
            }, 200);
        },
        onAnimationEnd(event) {
            if (event.target !== this.$refs.card || !this.isClosing) return;
            this.isClosing = false;
            this.setIsPanelOpen(false);
        },
        onCloseClick(event) {
            if (event.target === this.$refs.main) this.close();
        },
        onKeydown(event) {
            if (event.key !== 'Escape' || !this.isPanelOpen) return;
            if (document.querySelector('body > .menu, .p-datepicker:not(.p-datepicker-inline), .card-container.popup-shown')) return;
            this.close();
        }
    },
    mounted() {
        this.watchInvestigations();
        window.addEventListener('keydown', this.onKeydown);
    },
    beforeUnmount() {
        this.clearTimers();
        window.removeEventListener('keydown', this.onKeydown);
        this.unwatchInvestigations();
        this.setIsPanelOpen(false);
    }
}
</script>

<style lang="scss" scoped>
.ai-bubble {
    position: fixed;
    bottom: 25px;
    right: $page-gutter;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: $color-primary-blue;
    border: none;
    border-radius: 100%;
    width: 65px;
    height: 65px;
    padding: 0;
    cursor: pointer;
    transition: transform 0.2s ease, background-color 0.3s ease;
    z-index: 1200;

    &:hover {
        opacity: 0.9;
        transform: scale(1.05);

        .ai-icon {
            transform: scale(1.08);
        }
    }

    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px $color-card-back, 0 0 0 4px $color-primary-blue;
    }

    &.new-message {
        animation: btn-bounce 1.2s ease-in-out infinite;
    }

    &.new-message::before {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        transform: translate(-50%, -50%) scale(1);
        border: 1px solid rgba($color-primary-blue, 1);
        opacity: 0;
        pointer-events: none;
        z-index: 0;
        animation: ripple 1.2s ease-out infinite;
    }

    .counter-message,
    .ai-icon {
        position: relative;
        z-index: 2;
    }

    .counter-message {
        position: absolute;
        top: -2px;
        right: -2px;
        min-width: 25px;
        height: 25px;
        border-radius: 999px;
        background: #ff3b30;
        color: #fff;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 12px;
        font-weight: 500;
        line-height: 20px;
        text-align: center;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
        transform-origin: center;
        transition: opacity 0.3s ease-in-out;
        animation: badge-pop 300ms ease-out both;
    }

    .ai-icon {
        width: 36px;
        height: 36px;
        color: white;
        stroke: white;
        fill: none;
        overflow: visible;

        :deep(.robot-eyes),
        :deep(.robot-antenna) {
            transform-box: fill-box;
            transform-origin: center;
        }

        :deep(.robot-eyes) {
            animation: robot-blink 2.5s ease-in-out infinite;
        }

        &.animated :deep(.robot-antenna) {
            animation: robot-antenna 0.9s ease-in-out infinite;
        }
    }

    @media (max-width: 768px) {
        bottom: 10px;
        right: 10px;
        width: 55px;
        height: 55px;

        .counter-message {
            top: -5px;
            right: -4px;
            min-width: 23px;
            height: 23px;
        }

        .ai-icon {
            width: 32px;
            height: 32px;
        }
    }
}

body:has(.absolute-btn) .ai-bubble {
    bottom: 95px;
}

.main {
    z-index: 9999;
    position: fixed;
    top: 0;
    left: 0;
    background: rgba(8, 10, 15, 0.65);
    backdrop-filter: blur(6px);
    width: 100vw;
    height: 100vh;
    display: flex;
    justify-content: right;

    .card-box {
        width: 65px;
        height: 65px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        position: absolute;
        bottom: 25px;
        border-radius: 16px 16px 30px 16px;
        right: $page-gutter;
        animation-duration: .26s;
        animation-fill-mode: forwards;
        transform-origin: bottom right;
        will-change: transform, background, border-radius, opacity;

        &.popup-shown { animation-name: PopFromBtnIn; }
        &.popup-hidden { animation-name: PopFromBtnOut; }
    }
}

.ai-card-content {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
}

.ai-card-body {
    flex: 1 1 auto;
    min-height: 0;
}

.header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 15px;
    background: $color-card-back;
    border-bottom: 1px solid $color-separator;

    .title {
        display: flex;
        align-items: center;
        gap: $default-edge-spacing;
        color: $color-primary-text;
        font-weight: 600;
        padding-left: 3px;
        letter-spacing: .2px;
    }

    .title-icon {
        flex: 0 0 auto;
        width: 22px;
        height: 22px;
        color: $color-primary-blue;
        stroke: $color-primary-blue;
        fill: none;

        :deep(.robot-eyes) {
            transform-box: fill-box;
            transform-origin: center;
            animation: robot-blink 2.5s ease-in-out infinite;
        }
    }

    .btn-top {
        padding: 0;
        display: flex;
        border: none;
        background: transparent;
        justify-content: center;
        align-items: center;
        color: $color-secondary-text;
        transition: all 0.3s ease-in-out;

        &:hover svg {
            transform: none !important;
            color: $color-primary-text;
        }

        &:focus-visible {
            outline: none;
            box-shadow: 0 0 0 1px $color-primary-blue;
            border-radius: 50%;
        }
    }

    .btn-rotate {
        transform: rotate(45deg);

        &:hover {
            transform: rotate(-135deg);
        }
    }
}

@keyframes robot-blink {
    0%, 90%, 100% { transform: scaleY(1); }
    94% { transform: scaleY(0.1); }
}

@keyframes robot-antenna {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.8); opacity: 0.35; }
}

@keyframes btn-bounce {
    0% { transform: scale(1); }
    15% { transform: scale(0.9); }
    35% { transform: scale(1.03); }
    55% { transform: scale(1); }
    100% { transform: scale(1); }
}

@keyframes ripple {
    0% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
    100% { opacity: 0; transform: translate(-50%, -50%) scale(1.35); }
}

@keyframes badge-pop {
    0% { transform: scale(0.6); opacity: 0; }
    60% { transform: scale(1.1); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
}

@keyframes PopFromBtnIn {
    0% {
        border-radius: 32.5px;
        background: $color-primary-blue;
    }
    20% {
        background: $color-primary-blue;
    }
    55% {
        background: $color-card-back;
    }
    100% {
        width: 400px;
        height: min(540px, calc(100vh - 60px));
        background: $color-card-back;
        border-radius: 16px 16px 30px 16px;
    }
}

@keyframes PopFromBtnOut {
    0% {
        width: 400px;
        height: min(540px, calc(100vh - 60px));
        background: $color-card-back;
    }
    15% {
        background: $color-card-back;
    }
    80% {
        opacity: 1;
    }
    95% {
        width: 65px;
        height: 65px;
        background: $color-primary-blue;
    }
    100% {
        border-radius: 40px;
        opacity: 0;
    }
}

@keyframes PopFromBtnInMobile {
    0% {
        right: 10px;
        bottom: 10px;
        width: 55px;
        height: 55px;
        border-radius: 27.5px;
        background: $color-primary-blue;
    }
    20% {
        background: $color-primary-blue;
    }
    55% {
        background: $color-card-back;
    }
    100% {
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        background: $color-card-back;
        border-radius: 0;
    }
}

@keyframes PopFromBtnOutMobile {
    0% {
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        border-radius: 0;
        background: $color-card-back;
    }
    15% {
        background: $color-card-back;
    }
    80% {
        opacity: 1;
    }
    95% {
        right: 10px;
        bottom: 10px;
        width: 55px;
        height: 55px;
        background: $color-primary-blue;
    }
    100% {
        right: 10px;
        bottom: 10px;
        border-radius: 40px;
        opacity: 0;
    }
}

.v-enter-active,
.v-leave-active {
    transition: all .2s ease;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}

@media (max-width: $screen-md) {
    .main {
        height: 100vh;
        height: 100dvh;

        .card-box {
            right: 0;
            bottom: 0;

            &.popup-shown { animation-name: PopFromBtnInMobile !important; }
            &.popup-hidden { animation-name: PopFromBtnOutMobile !important; }
        }
    }

    .header {
        min-height: 56px;
        padding-top: calc(10px + env(safe-area-inset-top));
    }

    .svg-close {
        width: 30px !important;
        height: 30px !important;
    }

    .header .title {
        font-size: 18px !important;
    }
}

.chat-enter-active,
.chat-leave-active {
    transition: all 0.3s ease;
}

.chat-enter-from,
.chat-leave-to {
    opacity: 0;
    transform: translateY(10px);
}

.chat-enter-to,
.chat-leave-from {
    opacity: 1;
    transform: translateY(0);
}
</style>
